/**
 * Step 1: drop options (force send) + shared static tag for all affected scheduled sent rows.
 */

import type { FC } from 'react';
import { Settings2, Sliders, Tag } from 'lucide-react';

import { DropOptionsSection, StaticTagsPickerSection } from '@/features/smswiser-campaigns/components/drop-modals';
import { ColumnHeader } from '@/features/smswiser-campaigns/components/ScaleDropModal/shared';

import type { useMultiCountrySentScheduledEditForm } from '../hooks/useMultiCountrySentScheduledEditForm';

type Form = ReturnType<typeof useMultiCountrySentScheduledEditForm>;

interface Props {
  form: Form;
  isBusy: boolean;
}

export const SentScheduledConfigStep: FC<Props> = ({ form, isBusy }) => (
  <div className="flex flex-col gap-5 p-5">
    <ColumnHeader icon={Settings2} title="Drop options" subtitle="Apply to all drops in this edit" />
    <div className="rounded-md bg-muted/30 p-2.5 text-sm">
      <p className="font-medium text-foreground">Scheduled sent — shared settings</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Gateway, TLD, and campaign overrides are set per country in the Countries step.
      </p>
    </div>
    <section className="space-y-2">
      <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Tag className="h-3.5 w-3.5" /> Static tag
      </h4>
      <StaticTagsPickerSection
        mode="single"
        selectedId={form.staticTagId}
        onChange={(id) => form.setStaticTagId(id)}
        allowUnchanged
        compact
        disabled={isBusy}
      />
    </section>
    <div className="shrink-0 rounded-lg border border-border/40">
      <div className="flex items-center gap-1.5 bg-muted/20 px-3 py-2">
        <Sliders className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Send options</span>
      </div>
      <div className="p-3">
        <DropOptionsSection
          hideTitle
          compact
          showOnlyForceSend
          allowUnchanged
          amount={0}
          forceSend={form.forceSend}
          setForceSend={form.setForceSend}
          sendInBatches="unchanged"
          setSendInBatches={() => {}}
          batchAmount={undefined}
          setBatchAmount={() => {}}
          batchDelay={undefined}
          setBatchDelay={() => {}}
          aiOptimizationEnabled={false}
          setAiOptimizationEnabled={() => {}}
          aiOptimizationType=""
          setAiOptimizationType={() => {}}
          aiOptimizationBatchAmount=""
          setAiOptimizationBatchAmount={() => {}}
          aiOptimizationMinimalValuePercent=""
          setAiOptimizationMinimalValuePercent={() => {}}
          aiOptimizationSendUntil=""
          setAiOptimizationSendUntil={() => {}}
          useOnlyNewDomains="unchanged"
          setUseOnlyNewDomains={() => {}}
          shortlink={form.shortlink}
          isBusy={isBusy}
        />
      </div>
    </div>
  </div>
);
